<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import Lucide from "@/components/Base/Lucide";
import Button from "@/components/Base/Button";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import api from "@/utils/axios";
import PageTitle from "@template/components/PageTitle/PageTitle.vue";

type Tile = {
  icon: string;
  title: string;
  accent: string;
  route?: string;
  action?: "card";
};

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const notify = useNotification();

const feeInfo = ref<any>(null);

const cardOpen = ref(false);
const cardLoading = ref(false);
const cardSrc = ref<string | null>(null);
const cardFilename = ref("carne-socio.pdf");

const roleLabel = computed(() => {
  const role = authStore.user?.role;
  if (role === "ADMIN") return t("roles.admin");
  if (role === "TECNICO") return t("roles.board");
  return t("roles.member");
});

const feeStatus = computed(() => feeInfo.value?.membership?.status ?? null);
const hasMembership = computed(
  () => !!feeInfo.value?.has_membership || !!feeInfo.value?.membership?.id
);
const feePaid = computed(() => !!feeInfo.value?.fee_paid || feeStatus.value === "paid");
const feeYearLabel = computed(() => feeInfo.value?.academic_year?.label || "");
const showNotMember = computed(() => feeInfo.value !== null && !hasMembership.value);
const showFeePending = computed(
  () => feeInfo.value !== null && hasMembership.value && !feePaid.value && feeStatus.value !== "exempt"
);

const tiles = computed<Tile[]>(() => [
  { icon: "Users", title: t("home.tile_family"), accent: "bg-primary", route: "my-family" },
  { icon: "CreditCard", title: t("home.tile_card"), accent: "bg-accent", action: "card" },
  { icon: "FileText", title: t("home.tile_docs"), accent: "bg-success", route: "documents" },
  { icon: "CalendarDays", title: t("home.tile_activities"), accent: "bg-warning", route: "my-activities" },
  { icon: "ShieldCheck", title: t("home.tile_privacy"), accent: "bg-info", route: "my-privacy" },
]);

const go = (name: string) => router.push({ name });

const revokeCard = () => {
  if (cardSrc.value?.startsWith("blob:")) {
    URL.revokeObjectURL(cardSrc.value);
  }
  cardSrc.value = null;
};

const closeCard = () => {
  cardOpen.value = false;
  cardLoading.value = false;
  revokeCard();
};

const openCard = async () => {
  if (!feePaid.value) {
    notify.error(t("common.error"), t("card.fee_not_paid"));
    go("my-family");
    return;
  }

  revokeCard();
  cardOpen.value = true;
  cardLoading.value = true;
  try {
    const res = await api.get("/fees/me/card", {
      responseType: "blob",
      params: { inline: 1 },
    });
    const contentType = String(res.headers["content-type"] || "");
    if (!contentType.includes("pdf")) {
      const text = await (res.data as Blob).text();
      try {
        const json = JSON.parse(text);
        notify.error(t("common.error"), json.message || t("card.download_error"));
      } catch {
        notify.error(t("common.error"), t("card.download_error"));
      }
      closeCard();
      return;
    }

    const disposition = String(res.headers["content-disposition"] || "");
    const match = disposition.match(/filename="?([^";]+)"?/i);
    if (match?.[1]) {
      cardFilename.value = match[1];
    }

    const blob =
      res.data instanceof Blob && res.data.type.includes("pdf")
        ? res.data
        : new Blob([res.data], { type: "application/pdf" });
    cardSrc.value = URL.createObjectURL(blob);
  } catch (e: any) {
    let message = t("card.download_error");
    const blob = e.response?.data;
    if (blob instanceof Blob) {
      try {
        const json = JSON.parse(await blob.text());
        if (json.message) message = json.message;
      } catch {
        /* ignore */
      }
    }
    notify.error(t("common.error"), message);
    closeCard();
  } finally {
    cardLoading.value = false;
  }
};

const downloadCard = () => {
  if (!cardSrc.value) return;
  const a = document.createElement("a");
  a.href = cardSrc.value;
  a.download = cardFilename.value || "carne-socio.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const onTile = (tile: Tile) => {
  if (tile.action === "card") {
    openCard();
    return;
  }
  if (tile.route) go(tile.route);
};

const loadFee = async () => {
  try {
    const res = await api.get("/fees/me");
    feeInfo.value = res.data.data;
  } catch {
    feeInfo.value = null;
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && cardOpen.value) closeCard();
};

onMounted(() => {
  loadFee();
  window.addEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  revokeCard();
});
</script>

<template>
  <div>
    <PageTitle :title="t('home.welcome')" :subtitle="roleLabel" />

    <div
      v-if="showNotMember"
      class="mt-6 rounded-2xl border-2 border-warning/70 bg-warning/15 px-5 py-4 shadow-sm"
      role="alert"
    >
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <Lucide icon="UserX" class="h-7 w-7 shrink-0 text-warning" />
          <div>
            <p class="text-sm font-semibold uppercase tracking-wide text-amber-800">
              {{ t("fees.not_member") }}
              <span v-if="feeYearLabel"> · {{ feeYearLabel }}</span>
            </p>
            <p class="mt-1 text-base font-medium text-slate-900">
              {{ t("fees.not_member_help") }}
            </p>
          </div>
        </div>
        <Button variant="primary" class="shrink-0" @click="go('my-family')">
          {{ t("fees.request_enrollment") }}
        </Button>
      </div>
    </div>

    <div
      v-else-if="showFeePending"
      class="mt-6 rounded-2xl border-2 border-danger/60 bg-danger/10 px-5 py-4 shadow-sm"
      role="alert"
    >
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <Lucide icon="AlertTriangle" class="h-7 w-7 shrink-0 text-danger" />
          <div>
            <p class="text-sm font-semibold uppercase tracking-wide text-danger">
              {{ t("card.fee_unpaid_banner_title") }}
              <span v-if="feeYearLabel"> · {{ feeYearLabel }}</span>
            </p>
            <p class="mt-1 text-base font-medium text-slate-900">
              {{ t("card.fee_not_paid") }}
            </p>
            <p class="mt-1 text-sm text-slate-700">
              {{ t("card.fee_unpaid_banner_body") }}
            </p>
          </div>
        </div>
        <Button variant="danger" class="shrink-0" @click="go('my-family')">
          {{ t("card.go_family") }}
        </Button>
      </div>
    </div>

    <div
      v-else-if="feePaid"
      class="mt-6 rounded-2xl border border-success/40 bg-success/10 px-5 py-3 text-sm text-slate-800"
    >
      <div class="flex items-center gap-2">
        <Lucide icon="BadgeCheck" class="h-5 w-5 text-success" />
        <span>
          {{ t("card.fee_paid_banner", { year: feeYearLabel }) }}
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-6">
      <button
        v-for="tile in tiles"
        :key="tile.title"
        type="button"
        class="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white/90 p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        @click="onTile(tile)"
      >
        <span
          class="flex h-12 w-12 items-center justify-center rounded-xl text-white"
          :class="tile.accent"
        >
          <Lucide :icon="tile.icon" class="h-6 w-6" />
        </span>
        <span class="font-medium text-slate-800">{{ tile.title }}</span>
      </button>
    </div>

    <!-- Visor do carné (PDF) -->
    <Teleport to="body">
      <div
        v-if="cardOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3 sm:p-6"
        @click.self="closeCard"
      >
        <div
          class="flex w-full max-w-3xl max-h-[92vh] flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-darkmode-600"
          role="dialog"
          aria-modal="true"
          :aria-label="t('card.view_title')"
          @click.stop
        >
          <div
            class="flex shrink-0 items-center gap-2 border-b border-slate-200/60 px-4 py-3 dark:border-darkmode-400"
          >
            <h2 class="min-w-0 flex-1 truncate text-base font-medium">
              {{ t("card.view_title") }}
            </h2>
            <Button
              variant="outline-primary"
              size="sm"
              :disabled="cardLoading || !cardSrc"
              @click="downloadCard"
            >
              <Lucide icon="Download" class="h-4 w-4 mr-1" />
              {{ t("card.download") }}
            </Button>
            <Button variant="outline-secondary" size="sm" @click="closeCard">
              {{ t("common.close") }}
            </Button>
          </div>
          <div class="min-h-0 flex-1 overflow-hidden bg-slate-100 dark:bg-darkmode-500">
            <div
              v-if="cardLoading"
              class="flex flex-col items-center justify-center gap-3 py-20 text-slate-500"
            >
              <Lucide icon="Loader2" class="h-10 w-10 animate-spin text-primary" />
              <span class="text-sm">{{ t("card.loading") }}</span>
            </div>
            <iframe
              v-else-if="cardSrc"
              :src="cardSrc"
              class="h-[75vh] w-full border-0 bg-white"
              :title="t('card.view_title')"
            />
            <div v-else class="py-16 text-center text-slate-500">
              {{ t("card.download_error") }}
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
